import PartnerModel from './../data_models/partner.model';
import LearnerModel from './../data_models/learner.model';
import DegreeList from './../data_models/degree_list.model';
import CanvasModel from './../data_models/canvas_user.model';
import CourseList from './../data_models/course_list.model';
import EnrollmentList from './../data_models/enrollment_list.model';
import PathwayCourseList from './../data_models/pathway_degree_course_list.model';
import { JOURNEY_CODES } from '../shared/constants.js';
import { createEncodedState } from '../util/state-utils.js';
import { handleRedirectParams, clearProcessedParams, getAttributionHeaders } from '../util/param-persistence.js';
import { extractApiMessage, GENERIC_API_ERROR } from '../util/api-error.js';
import { getPartnerHost, isPartnerHostOverridden, partnerHostSwitchUrl } from '../util/partner-host.js';
import { isRetiredEaSubdomain } from '../util/retired-hosts.js';

import auth, { AUTH_PROVIDERS } from '../services/auth-manager.service';
import customOAuthService from '../services/custom-oauth.service';
import Axios from 'axios';
import { captureMessage, setUser } from './sentry.service';
// Removed Vuex store import - using Pinia stores directly
import router from '../router';
import { Helper } from '../mixins/helpers';
import { Logger } from '../mixins/logger';
import { useUiStore } from '../stores/ui';

// Traffic-cop guard (ACC-328): decides whether to redirect a learner from their
// current subdomain to the one matching their partner record. Skips the redirect
// for partners on a retired host (hs, kupu, gems): the router sends those hosts to
// welcome, so redirecting there would loop. Exported for unit testing.
export function shouldFireTrafficCopRedirect({
  isProxy,
  partnerURLSubDomain,
  partnerURLSubdomainWithEnv,
  currentSubdomain
}) {
  return (
    !isProxy &&
    Boolean(partnerURLSubDomain) &&
    !isRetiredEaSubdomain(partnerURLSubDomain) &&
    currentSubdomain !== partnerURLSubdomainWithEnv
  );
}

// Aether OTP redirect gate (ULC-3055): decides whether getLearnerDetails should
// run the custom_oauth link/token flow. It must NOT fire while viewing-as,
// because learnerData is then the impersonated learner and we'd send a
// verification code to the learner's email instead of loading their dashboard.
// Only ASURITE-authenticated staff hit this path. Exported for unit testing.
export function shouldHandleAetherAuthRedirect({ useAetherFlow, isProxy, isAsuriteUser }) {
  return Boolean(useAetherFlow) && !isProxy && Boolean(isAsuriteUser);
}

// View-as identifier param (ULC-3125): OTP learners have no ASURITE, so support can
// view-as them by email. An email identifier is only valid in proxy mode and rides in
// emailId= — the backend rejects asuRite= values that aren't \w+ before any handler
// runs. Returns the leading query fragment for /api/learner. Exported for unit testing.
export function buildLearnerIdentifierParam({ userId, isAsuriteUser, isProxy }) {
  if (isProxy && typeof userId === 'string' && userId.includes('@')) {
    return `emailId=${encodeURIComponent(userId)}&`;
  }

  return isAsuriteUser || isProxy ? `asuRite=${encodeURIComponent(userId)}&` : '';
}

// Lookup failures the View As dialog renders inline (not found / duplicate) instead of
// the generic error page. ASURITE_NOT_FOUND is what the API now returns when a view-as
// target has no learner, rather than silently registering the admin (ULC-3359).
// Exported for unit testing.
export const VIEW_AS_LOOKUP_ERROR_CODES = Object.freeze([
  'EMAIL_NOT_FOUND',
  'EMAIL_MULTIPLE_ASSOCIATIONS',
  'ASURITE_NOT_FOUND',
  'VIEW_AS_NOT_AUTHORIZED'
]);

export function isViewAsLookupError(errorCode) {
  return VIEW_AS_LOOKUP_ERROR_CODES.includes(errorCode);
}

// One-shot guard so a Cognito re-authorize that comes back still-unusable falls
// through to the OTP instead of looping the learner through ASU sign-in.
const ASURITE_REAUTH_KEY = 'asuriteReauthAttempted';

class ConnectorService {
  constructor($http, $window, StateManagerService, $interval) {
    this._baseUrl = null;
    this._xApiKey = null;
    this._$http = $http;
    this._$window = $window;
    this._StateManagerService = StateManagerService;
    this._$interval = $interval;
    this.partnerData = null;
    this.learnerData = null;
    this.degreeList = new DegreeList();
    this.courseList = new CourseList();
    this.enrollmentList = new EnrollmentList();
    this.pathwayData = [];
    this.pathwayCourseList = new PathwayCourseList();
    this._tokenType = 'NONE';
    this._authorization = null;
    this._isProxy = false;
    this._proxyAsurite = '';
    // Loading state counter to prevent flickering with concurrent requests
    this._loadingCounter = 0;
    // Debounce timeout to prevent flickering between rapid sequential requests
    this._loadingHideTimeout = null;
  }

  get isSignupFlow() {
    return this._tokenType.toLowerCase() === 'aws' || this.learnerData._isFirstTimeUser;
  }

  set env(args) {
    this._baseUrl = import.meta.env.VITE_API_BASE_URL;
    this._xApiKey = import.meta.env.VITE_X_API_KEY;

    if (!this._baseUrl || !this._xApiKey) {
      throw new Error('Missing required environment variables: VITE_API_BASE_URL and VITE_X_API_KEY');
    }
  }

  set setProxyAsurite(arg) {
    this._proxyAsurite = arg;
  }

  set setProxy(arg) {
    this._isProxy = arg;
  }

  // ACC-228: _proxyAsurite is set at View As entry (viewAsDialog/enterViewAs), but a caller
  // resuming a session on a fresh page load — e.g. the ?viewAs= / sessionStorage restore
  // path — can run before that assignment lands. Fall back to the persisted audited
  // session's targetAsurite so those callers still resolve who's being viewed as, and cache
  // it onto _proxyAsurite so the normal path is hit on the next call.
  async getResolvedProxyAsurite() {
    if (this._proxyAsurite) {
      return String(this._proxyAsurite).trim();
    }

    const { useUserStore } = await import('@/stores/user');
    const fallbackAsurite = String(useUserStore().impersonationSession?.targetAsurite || '').trim();

    if (fallbackAsurite) {
      this._proxyAsurite = fallbackAsurite;
      return fallbackAsurite;
    }

    return '';
  }

  async _doConnect(apiPath, method, data, options = {}) {
    // skipServerErrorRedirect (ACC-439): opt out of the shared 500 -> server-error page
    // push. For a supplementary read the caller degrades from, that redirect would take
    // down a page that renders fine without the data.
    const {
      skipAuthRedirect = false,
      skipLoading = false,
      skipImpersonationHeaders = false,
      skipServerErrorRedirect = false
    } = options;

    // Show global loading spinner with counter to prevent flickering
    const uiStore = useUiStore();
    if (!skipLoading) {
      // Clear any pending hide timeout - we have a new request starting
      if (this._loadingHideTimeout) {
        clearTimeout(this._loadingHideTimeout);
        this._loadingHideTimeout = null;
      }

      this._loadingCounter++;
      // Only show spinner if this is the first request
      if (this._loadingCounter === 1) uiStore.setLoadingState(true);
    }

    try {
      const response = await this._$http({
        headers: { ...(await this.headers(skipImpersonationHeaders)), ...(options.headers || {}) },
        url: this.baseURL() + apiPath,
        method: method,
        data: data
      });
      if (!localStorage.getItem('x-app-version') && response && response.headers && response.headers['x-app-version']) {
        localStorage.setItem('x-app-version', response.headers['x-app-version']);
      }
      if (
        response &&
        response.headers &&
        response.headers['x-app-version'] &&
        localStorage.getItem('x-app-version') !== response.headers['x-app-version']
      ) {
        // configure the version in localstorage
        localStorage.setItem('x-app-version', response.headers['x-app-version']);
        // Reload the page if the version mismatches
        window.location.reload();
      }
      return response;
    } catch (error) {
      // Enhanced error logging for better debugging
      Logger.methods.logError('_doConnect', {
        url: this.baseURL() + apiPath,
        method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        stack: error.stack
      });

      // Safely extract error information
      const status = error.response?.status;
      const errorData = error.response?.data || {};
      const referenceId = errorData.reference_id || '';

      // Safely parse error code
      let errorCode = '';
      try {
        if (typeof errorData.message === 'string') {
          let parsedMessage = '';

          try {
            parsedMessage = JSON.parse(errorData.message);

            errorCode = parsedMessage.errorCode || '';
            // eslint-disable-next-line no-empty
          } catch (e) {}
        } else if (errorData.errorCode) {
          errorCode = errorData.errorCode;
        }
      } catch (parseError) {
        Logger.methods.logError('_doConnect - Error parsing response message', parseError);
      }

      // A 401 tagged source:'aether-bridge' is an upstream token rejection, not a real ea-app session expiry.
      const isAetherBridgeError = status === 401 && errorData.source === 'aether-bridge';

      // Handle network errors and 401 unauthorized
      // Skip auth redirect if the caller wants to handle errors gracefully
      if (
        !isAetherBridgeError &&
        !skipAuthRedirect &&
        (!error.response || error.code === 'NETWORK_ERROR' || status === 401)
      ) {
        if (!this._isProxy && this.learnerData?.useAetherFlow && auth.getActiveProviderType() === 'custom_oauth') {
          try {
            const customOAuthProvider = auth.getActiveProvider();
            await customOAuthProvider.silentAuth();
          } catch {
            // silentAuth failed — full OTP login required
          }
          setTimeout(() => auth.login('custom_oauth'), 200);
        } else {
          setTimeout(() => auth.login('asurite'), 200);
        }
        return;
      }

      // If skipAuthRedirect is true, throw error for caller to handle
      if (skipAuthRedirect && (!error.response || error.code === 'NETWORK_ERROR')) {
        throw error;
      }

      // Handle 500 server errors
      if (status === 500 && !skipServerErrorRedirect) {
        router.push({ name: 'server-error', query: { referenceId } });
        return;
      }

      // Re-throw the error with enhanced information for other methods to handle
      const enhancedError = new Error(error.message);
      enhancedError.response = error.response;
      enhancedError.status = status;
      enhancedError.errorCode = errorCode;
      enhancedError.referenceId = referenceId;
      enhancedError.originalError = error;

      throw enhancedError;
    } finally {
      // Decrement loading counter and hide spinner only when all requests complete
      if (!options.skipLoading) {
        this._loadingCounter = Math.max(0, this._loadingCounter - 1);

        // Only hide spinner when all requests are complete
        if (this._loadingCounter === 0) {
          // Add a small delay before hiding to prevent flickering with rapid sequential requests
          // If a new request starts during this delay, it will clear this timeout
          this._loadingHideTimeout = setTimeout(() => {
            // Double-check counter is still 0 (no new requests started during delay)
            if (this._loadingCounter === 0) uiStore.setLoadingState(false);

            this._loadingHideTimeout = null;
          }, 150); // 150ms delay to prevent flicker
        }
      }
    }
  }

  async headers(skipImpersonationHeaders = false) {
    const contactId = this.learnerData && this.learnerData.contactId ? this.learnerData.contactId : '';

    const baseHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-api-key': this._xApiKey,
      tokentype: this._tokenType,
      Authorization: this._authorization ? `Bearer ${this._authorization}` : '',
      ...(contactId && contactId !== 'undefined' && contactId !== 'null' ? { contactid: contactId } : {}),
      ...getAttributionHeaders()
    };

    if (skipImpersonationHeaders) return baseHeaders;

    // ACC-228: attach the impersonation token only while a view-as session is actually
    // confirmed active — not merely because an unexpired token happens to be sitting in
    // the store. Without the isActive check, a token from a session that's already been
    // superseded (or that the admin is no longer in) would keep riding along on every
    // request, including ones made back in the admin's own context.
    const { useViewAsStore } = await import('@/stores/viewAs');
    if (!useViewAsStore().isActive) return baseHeaders;

    const { useUserStore } = await import('@/stores/user');
    const impersonationToken = useUserStore().getImpersonationToken;
    if (impersonationToken) {
      baseHeaders['x-impersonation-active'] = 'true';
      baseHeaders['x-secondary-authorization'] = `Bearer ${impersonationToken}`;
    }

    return baseHeaders;
  }

  baseURL() {
    return this._baseUrl;
  }

  async initSecureSession(appLogin, options = {}) {
    const self = this;
    try {
      // Set environment from current host
      self.env = window.location.host;

      // Extract host parts once to avoid repeated splits
      const hostParts = getPartnerHost().split('.');
      const [subdomain, domain] = hostParts;
      const userId = auth.getUserId();

      // Load partner details
      await self.loadPartnerDetails(subdomain, domain, userId);

      // Set authorization
      await self.setAuthorization();

      // Extract URL parameters if not provided in options
      const urlParams = new URLSearchParams(window.location.search);
      const learnerOptions = {
        courseOfferingId: options.courseOfferingId || urlParams.get('course_offering_id'),
        utmSource: options.utmSource || urlParams.get('utm_source'),
        utmCampaign: options.utmCampaign || urlParams.get('utm_campaign'),
        utmContent: options.utmContent || urlParams.get('utm_content'),
        ...options
      };

      // Filter out null/undefined values
      const filteredOptions = Object.fromEntries(Object.entries(learnerOptions).filter(([, value]) => value != null));

      // Get learner details and return user info
      return await self.getLearnerDetails(userId, appLogin, filteredOptions);
    } catch (error) {
      Logger.methods.logError('initSecureSession', error);

      throw error;
    }
  }

  async loadPartnerDetails(partnerNameFromUrl, eaOrUlc, userId) {
    try {
      const queryParams = [];
      queryParams.push(`partnerName=${partnerNameFromUrl}`);

      if (userId) {
        const activeProviderType = auth.getActiveProviderType();
        if (activeProviderType === 'custom_oauth') {
          queryParams.push(`emailotp_custom_oauth_id=${userId}`);
        } else {
          queryParams.push(`asuRite=${userId}`);
        }
      }

      if (eaOrUlc === 'ea' || eaOrUlc === 'ulc') {
        queryParams.push(`eaOrUlc=${eaOrUlc}`);
      }

      let apiPath = `/api/partners?${queryParams.join('&')}`;
      const method = 'get';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch Partners Info');
      let response = await this._doConnect(apiPath, method);

      this.partnerData = new PartnerModel(response.data);

      // Update Pinia store for partner data
      const { useUserStore } = await import('@/stores/user');
      const userStore = useUserStore();
      userStore.setPartnerData((this.partnerData || {}).welcomePageData);

      return response;
    } catch (error) {
      Logger.methods.logError('loadPartnerDetails', error);
      if (error.toString().includes('Error: Network Error')) {
        // Test method only, need to see how many folks are facing this issue
        // this.loadError();

        router.push({ name: 'browser-error', query: { referenceId: error.response.data.reference_id } });
      }
      if (error.toString().includes('failed with status code 404')) {
        router.push({ name: 'error', query: { referenceId: error.response.data.reference_id } });
      }
      throw error;
    }
  }

  // loadError() {
  //   this._$http({
  //     headers: {
  //       'Content-Type': 'application/json'
  //     },
  //     url: 'https://762412c74987.ngrok.io',
  //     method: 'GET'
  //   });
  // }

  validateSignup(signupData) {
    const activeProviderType = auth.getActiveProviderType();
    if (activeProviderType === 'custom_oauth') {
      signupData.emailotp_custom_oauth_id = signupData.emailotp_custom_oauth_id || auth.getUserId();
    } else {
      signupData.asuRite = signupData.asuRite || auth.getUsername();
    }

    let self = this;

    return new Promise((resolve, reject) => {
      let apiPath = `/api/learner`,
        method = 'post';
      const caller = Helper.methods.getCaller();
      const path = apiPath.split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Learners Registration');

      signupData.partnerId = self.partnerData.partnerId;
      signupData.corpTAndCRequiredForNewLearners = self.partnerData.corpTAndCRequiredForNewLearners;
      signupData.status = self.partnerData.status;

      self
        ._doConnect(apiPath, method, signupData)
        .then((response) => {
          // self._authorization = self._authorization || response.data.Authorization;
          // self._tokenType =
          //   response.data.tokentype && self._tokenType === 'NONE' ? response.data.tokentype : self._tokenType;

          self.learnerData = new LearnerModel(response.data);
          self._StateManagerService.userContactId = self.learnerData.contactId;
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }

  // ULC-3349 — "you may already have an account" step. Rides POST /api/learner behind an
  // explicit flag rather than a new route, so no API Gateway change is needed.
  async _asuriteAccountMatch(payload) {
    const response = await this._doConnect(
      '/api/learner',
      'post',
      { isAsuriteAccountMatch: true, ...payload },
      // The whole step is advisory: a redirect to the 500 page or back through login would
      // cost the learner the form they just filled in.
      { skipAuthRedirect: true, skipServerErrorRedirect: true, skipLoading: true }
    );

    return response?.data || {};
  }

  /**
   * Fuzzy duplicate check on last name + birth date. Returns masked candidates, never
   * addresses or contact ids. Swallows failures: a broken lookup must not stop a signup.
   */
  async findExistingAccountMatches({ lastName, birthDate, asuRite }) {
    try {
      const data = await this._asuriteAccountMatch({ action: 'check', lastName, birthDate, asuRite });

      return data.matches || [];
    } catch (error) {
      Logger.methods.logError('findExistingAccountMatches', error);

      return [];
    }
  }

  /** Emails a code to the candidate's stored address. Resolves to { otpId, maskedEmail }. */
  async sendExistingAccountCode({ lastName, birthDate, candidateRef }) {
    try {
      const data = await this._asuriteAccountMatch({
        action: 'sendOtp',
        lastName,
        birthDate,
        candidateRef
      });

      if (!data.otpId) throw new Error(GENERIC_API_ERROR);

      return data;
    } catch (error) {
      throw new Error(extractApiMessage(error));
    }
  }

  /** Verifies the code and moves the ASURITE onto the existing contact. */
  async linkAsuriteToExistingAccount({ lastName, birthDate, candidateRef, asuRite, otpId, otpCode }) {
    try {
      const data = await this._asuriteAccountMatch({
        action: 'link',
        lastName,
        birthDate,
        candidateRef,
        asuRite,
        otpId,
        otpCode
      });

      if (!data.linked) throw new Error(GENERIC_API_ERROR);

      return data;
    } catch (error) {
      throw new Error(extractApiMessage(error));
    }
  }

  postIDVerificationImages(body) {
    let self = this;

    return new Promise((resolve, reject) => {
      let apiPath = `/api/learner/id-verify`,
        method = 'post';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Posting ID Verification Images');
      self
        ._doConnect(apiPath, method, body)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  initiateIdVerification(body) {
    let self = this;

    return new Promise((resolve, reject) => {
      let apiPath = `/v2/api/learner/id-verify/initiate`,
        method = 'post';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Initiate magic-link IDV');
      self
        ._doConnect(apiPath, method, body)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  confirmIdVerification(body) {
    let self = this;

    return new Promise((resolve, reject) => {
      let apiPath = `/v2/api/learner/id-verify/confirm`,
        method = 'post';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Confirm magic-link IDV');
      self
        ._doConnect(apiPath, method, body)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getCourseList() {
    if (!this.learnerData) return Promise.resolve({});

    return new Promise((resolve, reject) => {
      let apiPath = `/api/degree-courses?partnerId=${this.learnerData.partnerId}&proxyAsurite=${this._proxyAsurite}`,
        method = 'get';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch Partners Courses');
      this._doConnect(apiPath, method, this.learnerData.basicLearnerInformation)
        .then((response) => {
          this.courseList.courses = response.data;

          resolve(
            Object.assign(
              {},
              this.courseList.courseListPageData,
              this.learnerData.courseListPageData,
              this.partnerData.courseListPageData
            )
          );
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  prepareEnrollCourseInCanvasBody(canvasUserId) {
    // TODO: @Rahul to update the api_OEAEnrollmentController and not send _canvasUserId in enrollment API.
    return new CanvasModel(this.learnerData, canvasUserId, 'enrollment');
  }

  getPathwayDegreeCourseList() {
    return new Promise((resolve, reject) => {
      if (this.learnerData.pathwayId) {
        let apiPath = `/api/degree-courses/pathways?partnerId=${this.learnerData.partnerId}&pathwayId=${this.learnerData.pathwayId}`,
          method = 'get';
        const caller = Helper.methods.getCaller();
        const path = (apiPath || '').split('?')[0];
        const journey = Helper.methods.getLearnersJourney(caller, path);
        apiPath = Helper.methods.setJourneyInResoursePath(
          apiPath,
          JOURNEY_CODES[journey],
          'Fetch Pathway Degree Courses'
        );
        this._doConnect(apiPath, method)
          .then((response) => {
            this.pathwayCourseList.pathwayCourses = response.data[0];
            resolve(
              Object.assign(
                {},
                this.pathwayCourseList.pathwayCourseListPageData,
                this.learnerData.pathwayCourseListPageData,
                this.partnerData.pathwayCourseListPageData
              )
            );
            // Update Pinia store for pathway data
            import('@/stores/pathways').then(({ usePathwaysStore }) => {
              const pathwaysStore = usePathwaysStore();
              pathwaysStore.setPathwayData(this.pathwayCourseList.pathwayCourseListPageData);
            });
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(Object.assign({}, {}, {}, {}));
      }
    });
  }

  /**
   * Get the user's enrolled pathway with full course and enrollment details
   * New endpoint that combines pathway data with user's enrollment status
   * @returns {Promise} - Pathway data with enrollment status, credit completion, and course details
   */
  getMyPathway() {
    return new Promise((resolve, reject) => {
      const contactId = this.learnerData?._contactId || this.learnerData?.contactId;

      if (!contactId) {
        resolve({ state: 'not_enrolled', message: 'No contact ID available' });
        return;
      }

      const learnerId = this.learnerData?._learnerId || this.learnerData?._learnerApplicationId;
      let apiPath = `/api/pathways/my-pathway?contactId=${contactId}&ea_learner_application=${learnerId}`;
      const method = 'get';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch My Pathway');

      this._doConnect(apiPath, method, null, { skipAuthRedirect: true })
        .then(async (response) => {
          // Handle case where response is undefined (CORS/network error)
          if (!response || !response.data) {
            resolve({ state: 'not_enrolled', message: 'API not available' });
            return;
          }
          const data = response.data;

          // Update Pinia store with the new pathway data structure
          // Must await the dynamic import to ensure store is updated before resolving
          const { usePathwaysStore } = await import('@/stores/pathways');
          const pathwaysStore = usePathwaysStore();
          pathwaysStore.setMyPathwayData(data);

          resolve(data);
        })
        .catch((error) => {
          Logger.methods.logError('getMyPathway', error);
          // Fail gracefully - return not_enrolled state so frontend doesn't crash
          resolve({ state: 'not_enrolled', message: 'API not available' });
        });
    });
  }

  /**
   * Fetch all dashboard sections (pathways + certificates) in a single call
   * @returns {Promise} - { success, pathways, certificates, _meta }
   */
  async getDashboard() {
    const contactId = this.learnerData?._contactId || this.learnerData?.contactId;
    const learnerId = this.learnerData?._learnerId || this.learnerData?._learnerApplicationId;

    let apiPath = `/v2/api/dashboard?ea_learner_application=${learnerId}`;
    if (contactId) {
      apiPath += `&contactId=${contactId}`;
    }

    const response = await this._doConnect(apiPath, 'get', null, { skipAuthRedirect: true });
    return response?.data || response;
  }

  /**
   * Get pathway course prefixes for the current contact
   * Lightweight endpoint for marking courses that align with pathway
   * @returns {Promise} - { enrolled, prefixes: [...], pathwayId, pathwayName }
   */
  getPathwayCoursePrefixes() {
    return new Promise((resolve, reject) => {
      const contactId = this.learnerData?._contactId || this.learnerData?.contactId;

      if (!contactId) {
        resolve({ enrolled: false, prefixes: [] });
        return;
      }

      let apiPath = `/api/pathways/course-prefixes?contactId=${contactId}`;
      const method = 'get';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(
        apiPath,
        JOURNEY_CODES[journey],
        'Fetch Pathway Course Prefixes'
      );

      this._doConnect(apiPath, method, null, { skipAuthRedirect: true })
        .then((response) => {
          // Handle case where response is undefined (CORS/network error)
          if (!response || !response.data) {
            resolve({ enrolled: false, prefixes: [] });
            return;
          }
          resolve(response.data);
        })
        .catch((error) => {
          Logger.methods.logError('getPathwayCoursePrefixes', error);
          // Fail gracefully - return empty prefixes so frontend doesn't crash
          resolve({ enrolled: false, prefixes: [] });
        });
    });
  }

  /**
   * Fetch pathway catalog data
   * @returns {Promise<Object>} Catalog data with pathways array
   */
  getPathwayCatalog() {
    // Ensure environment is set before making API call
    if (!this._baseUrl) {
      this.env = window.location.host;
    }

    return new Promise((resolve, reject) => {
      // Pass partnerId to get partner-filtered catalog
      const partnerId = this.learnerData?.partnerId;
      let apiPath = '/api/pathways/catalog';
      if (partnerId) {
        apiPath += `?partnerId=${partnerId}`;
      }
      const method = 'get';

      this._doConnect(apiPath, method, null, { skipAuthRedirect: true })
        .then((response) => {
          if (!response || !response.data) {
            resolve({ pathways: [], totalCount: 0 });
            return;
          }
          resolve(response.data);
        })
        .catch((error) => {
          Logger.methods.logError('getPathwayCatalog', error);
          // Fail gracefully - return empty catalog so frontend doesn't crash
          resolve({ pathways: [], totalCount: 0 });
        });
    });
  }

  refreshAndAddEnrollmentToSFForSelectedCourses(selectedCourses) {
    return new Promise((resolve, reject) => {
      let apiPath = `/api/enrollments?pretty&learnerId=${this.learnerData.learnerId}`,
        method = 'get';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);

      apiPath = Helper.methods.setJourneyInResoursePath(
        apiPath,
        JOURNEY_CODES[journey],
        'Selected Course Enrollment/Unenrollment'
      );

      this._doConnect(apiPath, method)
        .then((response) => {
          this.enrollmentList.existingActiveEnrollments =
            response?.data?.data?.enrollments && response?.data?.data?.enrollments instanceof Array
              ? response.data.data.enrollments
              : [response.data.data];

          this.createCourseConnectionInSalesforce(selectedCourses, resolve, reject);
        })
        .catch((error) => reject(error));
    });
  }

  patchEnrollmentsData(data) {
    return new Promise((resolve, reject) => {
      let apiPath = `/api/enrollments`;
      let method = 'patch';

      if (data instanceof Array) {
        for (const item of data) {
          item['isCourseResetRequested'] = item['isCourseResetRequested'] || false;
        }
      }
      const caller = Helper.methods.getCaller();
      const path = apiPath;
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Update Enrollment Info');

      this._doConnect(apiPath, method, data)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getLearnerConsentStatus(category) {
    return new Promise((resolve, reject) => {
      if (typeof category !== 'string' || !category.trim()) {
        reject(new Error('getLearnerConsentStatus requires a non-empty category string'));
        return;
      }
      let apiPath = `/v2/api/learner-consent/status?category=${encodeURIComponent(category)}`;
      const learnerId = this.learnerData?.learnerId;
      if (learnerId) apiPath += `&ea_learner_application=${encodeURIComponent(learnerId)}`;
      let method = 'get';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(
        apiPath,
        JOURNEY_CODES[journey],
        'Fetch Learner Consent Status'
      );
      this._doConnect(apiPath, method)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  submitLearnerConsent(data) {
    return new Promise((resolve, reject) => {
      const learnerId = this.learnerData?.learnerId;
      let apiPath = learnerId
        ? `/v2/api/learner-consent/submit?ea_learner_application=${encodeURIComponent(learnerId)}`
        : '/v2/api/learner-consent/submit';
      let method = 'post';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Submit Learner Consent');
      this._doConnect(apiPath, method, data)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // ULC-3523: graduation date gate. A supplementary read: errors (incl. 404 while the
  // API flag is off) reach the caller, which fails open, and never redirect the page.
  async getHsGraduationDateStatus() {
    const learnerId = this.learnerData?.learnerId;
    if (!learnerId) throw new Error('getHsGraduationDateStatus requires a learnerId');

    let apiPath = `/v2/api/learners/${encodeURIComponent(learnerId)}/hs-graduation-date`;
    const caller = Helper.methods.getCaller();
    const journey = Helper.methods.getLearnersJourney(caller, apiPath);
    apiPath = Helper.methods.setJourneyInResoursePath(
      apiPath,
      JOURNEY_CODES[journey],
      'Fetch High School Graduation Date Status'
    );
    const response = await this._doConnect(apiPath, 'get', undefined, {
      skipLoading: true,
      skipAuthRedirect: true,
      skipServerErrorRedirect: true
    });
    return response?.data?.data;
  }

  // answer: { graduationDate: 'YYYY-MM' } or { declined: true }
  async saveHsGraduationDate(answer) {
    const learnerId = this.learnerData?.learnerId;
    if (!learnerId) throw new Error('saveHsGraduationDate requires a learnerId');

    let apiPath = `/v2/api/learners/${encodeURIComponent(learnerId)}/hs-graduation-date`;
    const caller = Helper.methods.getCaller();
    const journey = Helper.methods.getLearnersJourney(caller, apiPath);
    apiPath = Helper.methods.setJourneyInResoursePath(
      apiPath,
      JOURNEY_CODES[journey],
      'Save High School Graduation Date'
    );
    const response = await this._doConnect(apiPath, 'patch', answer, {
      skipAuthRedirect: true,
      skipServerErrorRedirect: true
    });
    // Never report a save that wasn't confirmed (e.g. a swallowed network error).
    if (!response?.data?.data) throw new Error('Graduation date save returned no status');
    return response.data.data;
  }

  closeConfettiDialogMessage(data) {
    return new Promise((resolve, reject) => {
      let apiPath = `/api/learner?asuRite=${this.learnerData._asuRite}&learnerId=${this.learnerData.learnerId}`;
      let method = 'patch';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Update Confetti Info');

      this._doConnect(apiPath, method, data)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async patchLearnersInfo(data, learnerId) {
    // Set environment to ensure baseURL is available
    this.env = window.location.host;

    // Set authorization
    await this.setAuthorization();

    const queryParams = [];

    if (this.learnerData?._asuRite) {
      queryParams.push(`asuRite=${this.learnerData._asuRite}`);
    }
    if (this.learnerData?.learnerId || learnerId) {
      queryParams.push(`learnerId=${this.learnerData?.learnerId || learnerId}`);
    }

    let apiPath = `/api/learner?${queryParams.join('&')}`;

    let method = 'patch';

    const caller = Helper.methods.getCaller();
    const path = (apiPath || '').split('?')[0];
    const journey = Helper.methods.getLearnersJourney(caller, path);
    Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Update Learners Info');

    const response = await this._doConnect(apiPath, method, data);
    return response.data;
  }

  async courseCompletionConfetti(courses) {
    try {
      let completedCoursesPromises = [];

      courses.map((course) => {
        completedCoursesPromises.push(this.patchLearnersInfo(course));
      });

      return await Promise.all(completedCoursesPromises);
    } catch (error) {
      Logger.methods.logError('courseCompletionConfetti ' + error.stack);
    }
  }

  async createCourseConnectionInSalesforce(selectedCourses, resolve, reject) {
    // Get admin context from Pinia store
    const { useUserStore } = await import('@/stores/user');
    const userStore = useUserStore();
    const adminContext = userStore.adminContext;

    // Determine if admin is enrolling on behalf of learner
    const isAdminEnrolling = adminContext.asurite && adminContext.role;

    selectedCourses.forEach((selectedCourse) => {
      const enrollmentDate = Helper.methods.getCourseEnrollmentDate(selectedCourse);

      // OS-5117 - removed to toLocaleString for enrollmentDate, it is causing issues while registering courses
      this.courseList.addSelectedCourse({
        enrollmentId: null,
        courseOfferingId: selectedCourse._courseOfferingId,
        learnerId: this.learnerData.learnerId,
        contactId: this.learnerData.contactId,
        enrollmentStatus: 'Active', // This should never set to Inactive, since that's part of Unenroll journey already
        enrollmentDate: enrollmentDate,
        enrollmentMode: this.getEnrollmentMode({
          _enrollmentMode: selectedCourse._enrollmentMode,
          _courseInfo: {
            _courseType: selectedCourse._courseType
          }
        }),
        courseType: selectedCourse._courseType,
        enrolledByAsurite: isAdminEnrolling ? adminContext.asurite : this.learnerData._asuRite,
        enrolledByPersonRole: isAdminEnrolling ? adminContext.role : 'Student',
        enrollmentType: selectedCourse._enrollmentType
      });
    });

    let apiPath = `/api/enrollments`;
    const method = 'post';
    const caller = Helper.methods.getCaller();
    const path = (apiPath || '').split('?')[0];
    const journey = Helper.methods.getLearnersJourney(caller, path);

    apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Enroll In Selected Courses');

    return this._doConnect(apiPath, method, this.courseList.selectedCourses)
      .then((response) => {
        const rawEnrollments = response.data.enrollments ? response.data.enrollments : [response.data];
        this.enrollmentList.enrollments = rawEnrollments;

        this.courseList.clearSelectedCourses();

        if (resolve) resolve(this.enrollmentList.courseListPageData);
        return rawEnrollments;
      })
      .catch((error) => {
        if (reject) reject(error);
        else throw error;
      });
  }

  async unenrollCourse(data) {
    // Get admin context from Pinia store
    const { useUserStore } = await import('@/stores/user');
    const userStore = useUserStore();
    const adminContext = userStore.adminContext;

    // Determine if admin is unenrolling on behalf of learner
    const isAdminUnenrolling = adminContext.asurite && adminContext.role;

    // Add unenroll-specific fields
    const payload = {
      ...data,
      isUnenroll: true,
      unEnrollByPersonAsurite: isAdminUnenrolling ? adminContext.asurite : this.learnerData._asuRite,
      unEnrollByPersonRole: isAdminUnenrolling ? adminContext.role : 'Learner'
    };

    return new Promise((resolve, reject) => {
      let apiPath = `/api/enrollments`;
      let method = 'post';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Unenroll In Selected Courses');

      this._doConnect(apiPath, method, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  shouldCollectDemographicInfo() {
    return this.partnerData.collectDemographicInfo;
  }

  getBasicLearnerInformation() {
    return Object.assign({}, this.learnerData.basicLearnerInformation, this.partnerData.basicLearnerInformation);
  }

  getDemographicInformation() {
    return Object.assign({}, this.learnerData.demographicInformation);
  }

  getBasicLearnerInfo() {
    return Object.assign({}, this.learnerData.basicLearnerInfo);
  }

  getEducationalInformation() {
    return Object.assign({}, this.learnerData.educationalInformation);
  }

  validateEmail(hash) {
    let deferred = this._$q.defer();
    let apiPath = `/api/validateEmail?hash=${hash}`,
      method = 'get';
    this._doConnect(apiPath, method)
      .then((response) => {
        deferred.resolve(response.data);
      })
      .catch((error) => {
        deferred.reject(error);
      });
    return deferred.promise;
  }

  getPathwayData(partnerId, pathwayId) {
    this.env = window.location.host;

    return new Promise((resolve, reject) => {
      let apiPath = `/api/degree-courses/pathways?pathwayId=${pathwayId}&partnerId=${partnerId}`,
        method = 'get';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch Pathways Info');

      this._doConnect(apiPath, method)
        .then((data) => {
          this.pathwayData = data.data[0];
          // Update Pinia store for pathway data
          import('@/stores/pathways').then(({ usePathwaysStore }) => {
            const pathwaysStore = usePathwaysStore();
            pathwaysStore.setPathwayData(this.pathwayData);
          });
          resolve(data);
        })
        .catch((error) => {
          if (error.toString().includes('Error: Network Error')) {
            router.push({ name: 'browser-error', query: { referenceId: error.response.data.reference_id } });
          }

          reject(error);
        });
    });
  }

  /**
   * TODO : change partnerId
   */
  getDegreeSelectionData() {
    return new Promise((resolve, reject) => {
      let apiPath = `/api/partner-degrees?partnerId=${this.partnerData.partnerId}`,
        method = 'get';
      this._doConnect(apiPath, method)
        .then((data) => {
          this.degreeList.degrees = data.data;
          resolve(Object.assign({}, this.degreeList.degrees, this.partnerData.degreePageData));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  setActiveDegree(degree) {
    this.degreeList.activeDegree = degree;

    return Promise.resolve();
  }

  getDegreePaths() {
    let arr = Array.from(
      new Set(
        this.courseList._courses
          .reduce((a, b) => a + ';' + b._sessionName, '')
          .slice(1)
          .split(';')
      ).keys()
    );

    if (arr.indexOf('Self Paced') >= 0) {
      arr.splice(arr.indexOf('Self Paced'), 1);
      arr.unshift('Self Paced');
    }
    return arr;
  }

  getStudentName() {
    return (this.learnerData._firstName ? this.learnerData._firstName + ' ' : '') + this.learnerData._lastName;
  }

  getActiveDegree() {
    return this.degreeList.activeDegree;
  }

  getLoginPageData() {
    return Promise.resolve(this.partnerData.loginPageData);
  }

  verifyLearnerDetails(userId) {
    return new Promise((resolve, reject) => {
      const activeProviderType = auth.getActiveProviderType();
      let apiPath = `/api/learner/verify?${activeProviderType === 'custom_oauth' ? `emailotp_custom_oauth_id=${userId}` : `asuRite=${userId}`}&partnerId=${
          this.partnerData.partnerId
        }`,
        method = 'get';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Verify Learners Info');
      this._doConnect(apiPath, method)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Check if an ASURITE user needs to be redirected through the custom_oauth
   * OTP flow for Aether ID linking or token swap.
   * @returns {boolean} true if a redirect was triggered (caller should return early)
   */
  async _handleAetherAuthRedirect(activeProviderType) {
    // ULC-3055: gate also skips the flow while viewing-as (this._isProxy).
    if (
      !shouldHandleAetherAuthRedirect({
        useAetherFlow: this.learnerData?._useAetherFlow,
        isProxy: this._isProxy,
        isAsuriteUser: activeProviderType === AUTH_PROVIDERS.ASURITE
      })
    ) {
      return false;
    }

    const hasAetherId = !!this.learnerData?.emailotpCustomOauthId;
    const eaOptIn = this.learnerData?._eaOptIn;

    // Case 1: User has no Aether ID — trigger linking flow.
    // Outcome-based re-entry: the only state that should suppress this redirect
    // is `hasAetherId === true`. The previous flag-based guard left users stuck
    // when they abandoned the OTP page in the same tab (the sessionStorage flag
    // was set before redirect but cleared only by Callback.vue on success).
    // The aetherLinkFlow sessionStorage payload is still written — Callback.vue
    // reads it to resume the flow after OTP completion.
    if (!hasAetherId && eaOptIn !== false) {
      sessionStorage.setItem(
        'aetherLinkFlow',
        JSON.stringify({
          email: this.learnerData?._emailId,
          learnerId: this.learnerData?.learnerId,
          returnTo: 'dashboard'
        })
      );
      return await this._redirectToAetherAuth(this.learnerData?._emailId, 'missing-aether-id');
    }

    // Case 2: User already has Aether ID but needs a custom_oauth token for Stripe.
    // Outcome-based re-entry: presence + non-expiry of the token is the real
    // success signal. customOAuthService.isAuthenticated() validates both.
    if (hasAetherId && !customOAuthService.isAuthenticated()) {
      // ULC-3226: the stored aether session usually just needs re-hydrating after
      // a page load, or a background refresh via its 14-day refresh token. Neither
      // touches Cognito or navigates, so exhaust both before the full handshake.
      try {
        await customOAuthService.silentAuth({ redirectOnUnauthorized: false });
      } catch {
        // No stored session or the refresh token is dead — handshake below.
      }
      if (customOAuthService.isAuthenticated()) {
        sessionStorage.removeItem(ASURITE_REAUTH_KEY);
        return false;
      }

      sessionStorage.setItem(
        'aetherTokenFlow',
        JSON.stringify({
          email: this.learnerData?._emailId,
          learnerId: this.learnerData?.learnerId,
          returnTo: 'dashboard'
        })
      );
      return await this._redirectToAetherAuth(this.learnerData?._emailId, 'missing-oauth-token');
    }

    return false;
  }

  // ASURITE -> aether handoff (backend-to-backend). When the OTP-bypass flag is on,
  // exchange the Cognito ID token for aether tokens via the api-hub, store them on the
  // custom_oauth session, and link the aether id to the learner INLINE — no browser->
  // aether call, no session cookie, no authorize redirect. Returns false so the caller
  // continues loading the dashboard; falls back to the legacy emailotp redirect
  // (returns true) on any failure.
  async _redirectToAetherAuth(email, trigger = 'unknown') {
    const asuriteBypassEnabled = import.meta.env.VITE_ASURITE_AETHER_BYPASS_OTP === 'true';
    let fallbackReason = asuriteBypassEnabled ? 'unknown' : 'bypass-disabled';

    if (asuriteBypassEnabled) {
      try {
        const idToken = auth.getAsuriteIdToken();
        const tokenResponse = await customOAuthService.exchangeAsuriteTokenForTokens(idToken);
        // Store the aether tokens without emitting loginEvent (we continue inline
        // rather than re-entering the login handler).
        await customOAuthService.localLogin(tokenResponse, null, { emitEvent: false });

        // Tokens are stored at this point — a link failure must not bounce the
        // user to the OTP screen. Log and continue; the link retries next login.
        try {
          const aetherId = customOAuthService.getUserId();
          if (this.learnerData?.learnerId && aetherId) {
            await this.linkAetherIdToLearner(this.learnerData.learnerId, aetherId);
          }
        } catch (linkError) {
          Logger.methods.logError('ASURITE bypass: aether id link failed after token exchange', linkError);
          // Unlinked learners re-enter this flow on every login — the repeat-OTP driver.
          captureMessage('Aether id link failed after ASURITE token exchange', {
            level: 'warning',
            context: {
              tags: { aetherLink: 'failed', otpTrigger: trigger },
              extra: {
                learnerId: this.learnerData?.learnerId,
                errorMessage: linkError?.message
              }
            }
          });
        }

        // Inline path doesn't use the redirect resume flags; keep sessionStorage clean.
        sessionStorage.removeItem('aetherLinkFlow');
        sessionStorage.removeItem('aetherTokenFlow');
        sessionStorage.removeItem(ASURITE_REAUTH_KEY);

        return false;
      } catch (error) {
        if (error?.type === 'account_deactivated' || error?.code === 'account_deactivated') {
          useUiStore().setGlobalError({
            ...useUiStore().globalError,
            message: 'This account has been deactivated. Please contact our support team for assistance.'
          });
          router.push({ name: 'server-error' });
          return true;
        }
        // A stale/absent Cognito ID token is recoverable without any OTP: re-run the
        // ASURITE authorize redirect (silent while the ASU SSO session is alive) and
        // retry the exchange on the way back.
        if (error?.type === 'ASURITE_ID_TOKEN_EXPIRED' || error?.type === 'ASURITE_NO_ID_TOKEN') {
          Logger.methods.logInfo('ASURITE ID token unusable for the aether exchange, re-authorizing', error);
          if (this._reauthorizeAsuriteSession(trigger, error.type)) {
            return true;
          }
          fallbackReason = 'reauth-exhausted';
        } else {
          Logger.methods.logError('ASURITE->aether backend exchange failed, falling back to OTP', error);
          fallbackReason = error?.type || 'exchange-failed';
        }
        // fall through to the legacy emailotp redirect
      }
    }

    // A linked learner's token top-up must NEVER cost them an OTP: nothing outside
    // Stripe checkout consumes the aether token, so on any failure continue to the
    // dashboard and let the ladder retry on the next load (or at checkout).
    if (asuriteBypassEnabled && trigger === 'missing-oauth-token') {
      captureMessage('ASURITE aether token top-up failed; continuing without aether session', {
        level: 'warning',
        context: {
          tags: { otpFallback: 'suppressed', otpTrigger: trigger, otpFallbackReason: fallbackReason },
          extra: { learnerId: this.learnerData?.learnerId }
        }
      });
      sessionStorage.removeItem('aetherTokenFlow');
      return false;
    }

    // One event per verification code the learner did not ask for.
    captureMessage('Unrequested OTP: ASURITE bypass fell back to email OTP', {
      level: 'warning',
      context: {
        tags: { otpFallback: 'sent', otpTrigger: trigger, otpFallbackReason: fallbackReason },
        extra: { learnerId: this.learnerData?.learnerId }
      }
    });

    auth.login('custom_oauth', { email });
    return true;
  }

  // Cognito's implicit flow issues no refresh token, so the only way to replace an
  // expired ASURITE ID token is another authorize round trip. Returns true when the
  // redirect fired (caller stops), false when the one-shot guard is already spent.
  _reauthorizeAsuriteSession(trigger, tokenState) {
    if (sessionStorage.getItem(ASURITE_REAUTH_KEY)) {
      return false;
    }

    sessionStorage.setItem(ASURITE_REAUTH_KEY, 'true');

    captureMessage('ASURITE session re-authorized for the aether exchange', {
      level: 'info',
      context: {
        tags: { asuriteReauth: 'triggered', otpTrigger: trigger, asuriteTokenState: tokenState },
        extra: { learnerId: this.learnerData?.learnerId }
      }
    });

    // Come back to the page the learner was on, using the same mechanism the
    // router guard uses for unauthenticated deep links.
    if (!localStorage.getItem('requestedURL')) {
      const currentUrl = window.location.href.split(window.location.origin)[1];
      if (currentUrl && !currentUrl.startsWith('/callback')) {
        localStorage.setItem('requestedURL', currentUrl);
      }
    }

    auth.login(AUTH_PROVIDERS.ASURITE);
    return true;
  }

  async getLearnerDetails(userId, appLogin, options = {}) {
    try {
      appLogin = appLogin || true;

      const activeProviderType = auth.getActiveProviderType();
      const isAsuriteUser = activeProviderType === AUTH_PROVIDERS.ASURITE;
      // ULC-3125: in proxy mode an email identifier goes in emailId=, not asuRite=
      const identifierParam = buildLearnerIdentifierParam({
        userId,
        isAsuriteUser,
        isProxy: this._isProxy
      });
      let apiPath = `/api/learner?${identifierParam}partnerId=${this.partnerData.partnerId}&appLogin=${appLogin}`;

      // Add courseOfferingId if provided
      if (options.courseOfferingId) {
        apiPath += `&courseOfferingId=${options.courseOfferingId}`;
      }

      // Add UTM parameters if provided
      if (options.utmSource) {
        apiPath += `&utmSource=${options.utmSource}`;
      }
      if (options.utmCampaign) {
        apiPath += `&utmCampaign=${options.utmCampaign}`;
      }
      if (options.utmContent) {
        apiPath += `&utmContent=${encodeURIComponent(options.utmContent)}`;
      }

      const method = 'get';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch Learners Info');

      // Check pathway state from Pinia store
      const { usePathwaysStore } = await import('@/stores/pathways');
      const pathwaysStore = usePathwaysStore();
      if (userId && pathwaysStore.pathwayId) {
        pathwaysStore.setChangePathwayModal(true);
      }
      const response = await this._doConnect(apiPath, method);

      // Check if response indicates NO_CONTACT_FOUND (now returned as 200 instead of 404)
      if (response?.data?.message?.includes('In order to create a Pathway to Admission account')) {
        // Get userStore instance for error handling
        const { useUserStore } = await import('@/stores/user');
        const userStoreForError = useUserStore();
        if (!userStoreForError.welcomePageData.isEligibleForSignUp) {
          router.push({
            name: 'error',
            query: {
              referenceId: ''
            }
          });
        } else {
          const email = response.data.userEmail;

          router.push({ name: 'Registerv2', query: { email } });
        }
        return response?.data;
      }

      this.learnerData = new LearnerModel(response?.data);

      // Update Pinia store for learner data
      const { useUserStore } = await import('@/stores/user');
      const userStore = useUserStore();
      userStore.setLearnerData(this.learnerData);

      // Identify the user to Sentry. Internal ID only — no email, name, or
      // other PII. Always the DB contact id: the contact and learner-application
      // id spaces overlap, so a learner-application id here sends support to an
      // unrelated learner when they look it up as a contact.
      if (this.learnerData?._contactId) {
        setUser({ id: String(this.learnerData._contactId) });
      }

      // Check if ASURITE user needs Aether ID verification or token swap
      const redirected = await this._handleAetherAuthRedirect(activeProviderType);
      if (redirected) return;

      // Push user_id to dataLayer after successful learner data fetch
      if (typeof window !== 'undefined' && window.dataLayer && this.learnerData.contactSalesforceId) {
        window.dataLayer.push({
          user_id: this.learnerData.contactSalesforceId
        });
      }

      // Note: Pathway recommendations are now fetched via getMyPathway() endpoint
      // which returns all 3 states (new, recommendations, enrolled) in a single call

      const { protocol } = this._$window.location;
      const host = getPartnerHost(this._$window);
      // eslint-disable-next-line no-unused-vars
      const [subdomain, ...restHostParts] = host.split('.');

      // Handle persistent parameters across domain redirects
      const persistentParams = ['viewAs', 'course_offering_id'];

      // Traffic cop: redirect to correct subdomain if needed.
      // Retired hosts (hs, kupu, gems): those learners stay on welcome, see util/retired-hosts.
      if (
        shouldFireTrafficCopRedirect({
          isProxy: this._isProxy,
          partnerURLSubDomain: this.learnerData._partnerURLSubDomain,
          partnerURLSubdomainWithEnv: this.learnerData.partnerULRSubdomainWithEnv,
          currentSubdomain: subdomain
        })
      ) {
        // First manipulation: Replace subdomain with partner URL subdomain
        const newSubdomain = this.learnerData.partnerULRSubdomainWithEnv;
        let updatedHost = host.replace(subdomain, newSubdomain);

        const isWelcome = newSubdomain.split('-')[0] === 'welcome';

        updatedHost = updatedHost.replace(isWelcome ? 'ea' : 'ulc', isWelcome ? 'ulc' : 'ea');

        // Dev workspace (VITE_PARTNER_HOST): stay on this origin (the session survives) and reload
        // under the new virtual partner host, so nothing keeps the old partner's data.
        if (isPartnerHostOverridden()) {
          this._$window.location.replace(partnerHostSwitchUrl(this._$window.location, updatedHost));
          return;
        }

        const queryParams = new URLSearchParams();
        queryParams.set('redirect_source', subdomain);

        // Prepare state parameters
        const stateParams = {};
        if (this.learnerData._emailId) stateParams.email = this.learnerData._emailId;
        if (this.learnerData._asuRite) stateParams.asurite_id = this.learnerData._asuRite;

        let queryParamsFromRequestedURL = null;

        // Check for course_offering_id from localStorage (from requestedURL) - legacy support
        const requestedURL = localStorage.getItem('requestedURL');

        if (requestedURL) {
          try {
            // Handle both absolute and relative URLs
            const url = requestedURL.startsWith('http')
              ? new URL(requestedURL)
              : new URL(requestedURL, window.location.origin);
            queryParamsFromRequestedURL = new URLSearchParams(url.search);
          } catch (error) {
            Logger.methods.logError('Failed to parse requestedURL from localStorage', error);
          }
        }

        // Handle all persistent parameters using the utility
        const { redirectParams } = handleRedirectParams(persistentParams, queryParamsFromRequestedURL);

        // Add any found persistent parameters to state
        persistentParams.forEach((paramName) => {
          if (redirectParams[paramName]) {
            stateParams[paramName] = redirectParams[paramName];
          }
        });

        if (Object.keys(stateParams).length > 0) {
          const encodedState = createEncodedState(stateParams);
          queryParams.set('state', encodedState);
        }

        // Update the location with query string
        const queryString = queryParams.toString();
        const redirectURL = `${protocol}//${updatedHost}?${queryString}`;

        clearProcessedParams(persistentParams);

        this._$window.location.href = redirectURL;

        return;
      }

      return (response || {}).data;
    } catch (error) {
      const status = error.status || error.response?.status;
      const errorCode = error.errorCode || '';
      const referenceId = error.referenceId || '';
      const responseData = error.response?.data || {};

      // Handle specific error scenarios not handled by _doConnect
      if (errorCode === 'NO_CONTACT_FOUND' || responseData.message.indexOf('NO_CONTACT_FOUND') >= 0) {
        // Get userStore instance for error handling
        const { useUserStore } = await import('@/stores/user');
        const userStoreForError = useUserStore();
        if (!userStoreForError.welcomePageData.isEligibleForSignUp) {
          router.push({
            name: 'error',
            query: {
              referenceId: status === 401 || status === 403 ? '' : referenceId
            }
          });
        } else {
          const emailMatch = responseData.message.match(/X-User-Email:\s*([\w.-]+@[\w.-]+)/);

          const email = emailMatch ? emailMatch[1] : this.learnerData?._emailId;

          router.push({ name: 'Registerv2', query: { email } });
        }
        return;
      }

      // ULC-3125 / ULC-3359: view-as lookup failures are surfaced inline by the
      // View As dialog — rethrow instead of redirecting to the error page.
      if (isViewAsLookupError(errorCode)) {
        throw error;
      }

      // ACC-467/ULC-3181: a denied view-as attempt is an expected outcome, not an
      // application failure — surface it inline in the View As dialog instead of
      // tearing down the whole portal with the generic browser-error page.
      if (errorCode === 'VIEW_AS_NOT_AUTHORIZED') {
        throw error;
      }

      // Handle 4xx client errors (except those already handled)
      if (status >= 400 && status < 500 && errorCode !== 'NO_CONTACT_FOUND') {
        router.push({ name: 'browser-error', query: { referenceId } });
        return;
      }

      // Network errors without response
      if (!error.response && error.message?.includes('Network Error')) {
        router.push({ name: 'browser-error', query: { referenceId } });

        return;
      }

      throw error;
    }
  }

  /**
   * ACC-228: records an audited, expiring impersonation session alongside the legacy
   * asuRite-swap View As flow. Best-effort by design — the /api/learner-based flow
   * above is what actually renders the impersonated dashboard, so a failure here
   * (network, not-yet-authorized, target out of scope) must never block or unwind an
   * otherwise successful View As entry. Returns the session payload, or null on any
   * failure.
   */
  async createImpersonationSession({ actorLearnerId, targetLearnerId, targetAsurite, reason }) {
    try {
      if (!actorLearnerId || (!targetLearnerId && !targetAsurite)) return null;

      // skipImpersonationHeaders: minting a session must never carry a leftover token
      // from a prior one — the backend treats any impersonation-flagged request as
      // scoped to that session's own allowed routes, and this endpoint isn't one of
      // them, so a stale token here would 403 the very call meant to replace it.
      const response = await this._doConnect(
        '/v2/api/admin/impersonate',
        'post',
        { actorLearnerId, targetLearnerId, targetAsurite, reason },
        { skipAuthRedirect: true, skipServerErrorRedirect: true, skipLoading: true, skipImpersonationHeaders: true }
      );

      return response?.data?.data || null;
    } catch (error) {
      Logger.methods.logError('createImpersonationSession', error);

      return null;
    }
  }

  /**
   * Get courses for learner (V2 unified endpoint)
   * This replaces 4-5 separate API calls with a single optimized endpoint
   * @param {string} learnerId - EA Learner Application ID
   * @returns {Promise<Object>} Response with courses, filterOptions, summary
   */
  async getCoursesForLearnerV2(learnerId) {
    try {
      const apiPath = `/v2/api/learners/${learnerId}/courses`;
      const method = 'get';

      Logger.methods.logInfo('Fetching courses from V2 endpoint', { learnerId });

      const response = await this._doConnect(apiPath, method);

      Logger.methods.logInfo('Courses fetched successfully from V2', {
        totalCourses: response?.data?.summary?.totalCoursesAvailable || 0,
        enrolledCourses: response?.data?.summary?.enrolledCourses || 0
      });

      return response.data;
    } catch (error) {
      Logger.methods.logError('getCoursesForLearnerV2', error);
      throw error;
    }
  }

  async getEvents() {
    try {
      let apiPath = `/api/news-events`;
      let method = 'get';
      let response = await this._doConnect(apiPath, method);

      return response.data;
    } catch (error) {
      Logger.methods.logError('get', error);
    }
  }

  async updatePathwayCompletedDate(learnerId) {
    try {
      let apiPath = `/api/learner?learnerId=${learnerId}`;
      let method = 'patch';
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);

      await this._doConnect(apiPath, method, {
        pathwayCompletionDate: Date.now(),
        journey: JOURNEY_CODES[journey] || 'Update Pathways Completed Date'
      });
    } catch (error) {
      Logger.methods.logError('patch', error);
    }
  }

  async updatePathwayConnection(identifier, data) {
    try {
      // Support both contactId and asuRite for pathway enrollment
      // Prefer contactId if available, fallback to asuRite for backwards compatibility
      let apiPath;
      if (identifier && typeof identifier === 'object') {
        // New format: { contactId: '...' } or { asuRite: '...' }
        if (identifier.contactId) {
          apiPath = `/api/learner?contactId=${identifier.contactId}`;
        } else if (identifier.asuRite) {
          apiPath = `/api/learner?asuRite=${identifier.asuRite}`;
        } else {
          throw new Error('Either contactId or asuRite must be provided');
        }
      } else {
        // Legacy format: string asuRite
        apiPath = `/api/learner?asuRite=${identifier}`;
      }

      let method = 'patch';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);

      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Update Pathway Connection');

      let response = await this._doConnect(apiPath, method, data);

      // Update Pinia store for new pathway modal - only for enrollment, not unenrollment
      if (!data.isInactive) {
        const { usePathwaysStore } = await import('@/stores/pathways');
        const pathwaysStore = usePathwaysStore();
        pathwaysStore.setNewPathwayModal(true);
      }

      return response;
    } catch (error) {
      Logger.methods.logError('patch', error);
      throw error; // Re-throw to allow callers to handle specific errors
    }
  }

  /**
   * Link an Aether ID (custom OAuth ID) to an existing learner
   * Used when ASURITE users need to complete OTP verification for Aether ID linking
   * @param {string} learnerId - The learner's ID
   * @param {string} aetherId - The Aether/custom OAuth ID to link
   * @returns {Promise<Object>} - Response from the API
   */
  async linkAetherIdToLearner(learnerId, aetherId) {
    try {
      this.env = window.location.host;
      await this.setAuthorization();

      const apiPath = `/api/learner?learnerId=${learnerId}`;
      const method = 'patch';
      const data = { emailotpCustomOauthId: aetherId };

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Link Aether ID to Learner');

      const response = await this._doConnect(apiPath, method, data);

      // Update local learner data
      if (this.learnerData) {
        this.learnerData._emailotpCustomOauthId = aetherId;
      }

      return response.data;
    } catch (error) {
      Logger.methods.logError('linkAetherIdToLearner', error);
      throw error;
    }
  }

  getPartnerName() {
    return getPartnerHost(this._$window).split('.')[0].split('-')[0].toLowerCase();
  }

  getEnrollmentMode(enrollment) {
    if ((enrollment._enrollmentMode || enrollment._courseInfo._courseType || '').toLowerCase() === 'audit')
      return 'audit';

    if (enrollment._enrollmentMode === 'credit') {
      return 'credit';
    } else if (enrollment._enrollmentMode === 'verified') {
      return 'verified';
    } else {
      return 'honor';
    }
  }
  async postTransaction(enrollment, paymentMode, paypalResponse) {
    try {
      let apiPath = `/api/transaction`;
      let method = 'post';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);

      // Handle zero amount cases (from voucher or benefits)
      const finalAmount = parseFloat(enrollment._finalAmount || enrollment._amount || 0);
      const hasVoucher = enrollment._voucherCode && enrollment._voucherDiscount > 0;
      const isZeroAmount = finalAmount === 0;
      const isFullVoucherDiscount =
        hasVoucher && parseFloat(enrollment._voucherDiscount) >= parseFloat(enrollment._amount);

      // Determine payment mode:
      if (hasVoucher) {
        paymentMode = isZeroAmount || isFullVoucherDiscount ? 'free' : paymentMode;
      }

      // For free transactions, ensure we have transaction data
      if (paymentMode === 'free' && hasVoucher && (!paypalResponse || !paypalResponse.id)) {
        const uniqueId = 'free-' + Date.now();
        paypalResponse = {
          id: uniqueId,
          transactions: [
            {
              invoice_number: paypalResponse?.transactions?.[0]?.invoice_number || this.generateInvoiceNumber(),
              amount: { total: '0.00' }
            }
          ]
        };
      }

      if (!enrollment._courseInfo?._openEdxCourseId) {
        captureMessage('postTransaction missing openEdxCourseId', {
          level: 'error',
          context: {
            tags: {
              source: 'post_transaction_missing_openedx',
              paymentMode,
              enrollmentMode: enrollment._enrollmentMode,
              journey: JOURNEY_CODES[journey] || 'Create Transaction for Upgrade/Credit'
            },
            extra: {
              caller,
              courseInfoKeys: enrollment._courseInfo ? Object.keys(enrollment._courseInfo) : null,
              courseOfferingId:
                enrollment._courseInfo?._courseOfferingId || enrollment._courseInfo?.courseOfferingId || null,
              hasPaypalResponse: !!paypalResponse?.id,
              hasVoucher
            }
          }
        });
      }

      const response = await this._doConnect(apiPath, method, {
        username: this.learnerData._asuRite,
        contactId: this.learnerData._contactId || this.learnerData.contactId,
        paymentMode: paymentMode,
        courseInfo: {
          openEdxCourseId: enrollment._courseInfo._openEdxCourseId
        },
        enrollmentMode: enrollment._enrollmentMode,
        enrollmentId: enrollment._enrollmentId,
        // benefitsEligible: enrollment._benefitsEligible,
        // benefitType: enrollment._benefitType, // Rely on database to calculate this based on benefitsEligible and enrollmentMode
        ...(paypalResponse && {
          payer: paypalResponse ? paypalResponse.payer : '',
          transactions: paypalResponse ? paypalResponse.transactions : []
        }),
        transaction_id: paypalResponse?.id, // Explicitly include transaction ID
        ...(hasVoucher && {
          voucher_code: enrollment._voucherCode,
          voucher_price: enrollment._voucherDiscount,
          original_amount: enrollment._amount,
          final_amount: finalAmount,
          is_full_discount: isFullVoucherDiscount
        }),
        ...(enrollment._campaignCode && {
          campaign_code: enrollment._campaignCode
        }),
        isConsolidatedPricePaid: enrollment._benefitType === '5' && enrollment._enrollmentMode === 'credit',
        creditConversionPrice: enrollment._creditConversionPrice || 0,
        journey: JOURNEY_CODES[journey] || 'Create Transaction for Upgrade/Credit'
      });

      return response;
    } catch (error) {
      Logger.methods.logError('postTransaction ' + error.stack);
    }
  }

  // Helper method to generate invoice number
  generateInvoiceNumber() {
    let today = new Date().toISOString().slice(0, 10).replace(/-/g, '').slice(2);
    let prefix = import.meta.env.VITE_ENV_HOOK === 'prod' ? 'PRODOSPP' : 'OSPP';
    let random2Char = '';
    let random3Char = '';
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let charactersLength = characters.length;

    for (let i = 0; i < 2; i++) {
      random2Char += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    for (let i = 0; i < 3; i++) {
      random3Char += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return `${prefix}-${today}${Math.floor(Math.random() * 10)}${random2Char}${Math.floor(Math.random() * 100)}${random3Char}`;
  }

  async postCreditRequest(creditRequestPayload) {
    try {
      let apiPath = `/api/transaction/credit/request`;
      let method = 'post';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Credit Request');

      return (await this._doConnect(apiPath, method, creditRequestPayload)).data;
    } catch (error) {
      Logger.methods.logError('postCreditRequest ' + error.stack);
      return {
        errorMessage: error.response.data.message
      };
    }
  }

  async gradeChange(event) {
    try {
      let apiPath = `/api/enrollments/grade`;
      let method = 'post';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch Grade Change');

      const response = await this._doConnect(apiPath, method, [
        {
          enrollmentId: event._enrollmentId,
          openEdxCourseId: event._openEdxCourseId,
          asurite: this.learnerData._asuRite,
          grade: event._grade,
          canvasEnrollmentId: event._canvasEnrollmentId,
          canvasUserId: event._canvasUserId,
          canvasCourseId: event._canvasCourseId
        }
      ]);

      Logger.methods.logInfo('returning', response.data);

      return response.data;
    } catch (error) {
      Logger.methods.logError('gradeChange' + error);
    }
  }

  /**
   * The learner's enrollments. With includeScheduled, the same call also carries their queued
   * OD preregistrations, read the way api-hub's scheduled read always has: under View-as the
   * bearer is the staff member's, so the viewed learner is named and the staff ID token lets
   * api-hub attribute the read to the person. The result lands on enrollmentList.scheduled.
   *
   * @param {Object} [options]
   * @param {boolean} [options.includeScheduled]
   */
  getDashboardPageData({ includeScheduled = false } = {}) {
    if (!this.learnerData) return Promise.resolve({});

    return new Promise((resolve, reject) => {
      let self = this;

      let apiPath = `/api/enrollments?pretty&learnerId=${this.learnerData.learnerId}`,
        method = 'get';
      let scheduledHeaders = {};
      if (includeScheduled) {
        apiPath += '&includeScheduled=true';

        if (this._isProxy && this._proxyAsurite) {
          apiPath += `&proxyAsurite=${encodeURIComponent(this._proxyAsurite)}`;

          // Absent or expired, api-hub stays on its team token rather than failing the read.
          try {
            scheduledHeaders = { 'x-staff-identity-token': auth.getAsuriteIdToken() };
          } catch (error) {
            scheduledHeaders = {};
          }
        }
      }
      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch Learners Enrollments');
      if (Helper.methods.isAcceleratePartner(this.learnerData._partnerURLSubDomain))
        apiPath = Helper.methods.setCertificatesInResoursePath(apiPath, ['Mastery', 'Foundations']);

      this._doConnect(apiPath, method, null, { headers: scheduledHeaders })
        .then((response) => {
          const data = response?.data?.data || {};

          // { runs, error }: the scheduled read never fails the enrollments, so its failure
          // rides beside them for the caller to report. Undefined when it was not asked for.
          // An api-hub that ignores includeScheduled carries neither key; that is reported, not
          // read as "nothing queued".
          this.enrollmentList.scheduled = includeScheduled
            ? {
                runs: data.scheduled?.scheduled || [],
                error: data.scheduledError || (data.scheduled ? null : { code: 'SCHEDULED_NOT_CARRIED', status: null })
              }
            : undefined;
          this.enrollmentList.existingActiveEnrollments = [];
          this.enrollmentList.enrollments = data.enrollments ? data.enrollments : [data];
          this.enrollmentList.certificatesAndMastery = data.credentials ? data.credentials : [data];
          // This is for bachelor degrees
          this.enrollmentList.collegeCredits = data.creditsStatus ? data.creditsStatus : [data];
          this.enrollmentList.showLearnerProgress = data.showLearnerProgress ? data.showLearnerProgress : false;
          this.enrollmentList.estimatedCost = data.estimatedCost ? data.estimatedCost : [data];
          // This is for associate degrees
          this.enrollmentList.degreeProgress = data.degreeProgress ? data.degreeProgress : [data];
          this.enrollmentList.learnerUGApplicationStatus = data.learnerUGApplicationStatus
            ? data.learnerUGApplicationStatus
            : [data];
          this.enrollmentList.semesterYearProgress = data.semesterYearProgress ? data.semesterYearProgress : [data];

          // Update Pinia store for enrollment data
          import('@/stores/courses').then(({ useCoursesStore }) => {
            const coursesStore = useCoursesStore();
            coursesStore.setEnrollments(this.enrollmentList.enrollments);
            coursesStore.setCertificatesAndMastery(this.enrollmentList.certificatesAndMastery);
            coursesStore.setCollegeCredits(this.enrollmentList.collegeCredits);
            coursesStore.setShowLearnerProgress(this.enrollmentList.showLearnerProgress);
            coursesStore.setEstimatedSavings(this.enrollmentList.estimatedCost);
            coursesStore.setDegreeProgress(this.enrollmentList.degreeProgress);
            coursesStore.setLearnerUGApplicationStatus(this.enrollmentList.learnerUGApplicationStatus);
            coursesStore.setSemesterYearProgress(this.enrollmentList.semesterYearProgress);
          });

          resolve(
            Object.assign(
              {},
              self.enrollmentList.dashboardPageData,
              self.learnerData.dashboardPageData,
              self.partnerData.dashboardPageData
            )
          );
        })
        .catch((error) => reject(error));
    });
  }

  /**
   * To get the state from the URL Pathname.
   *
   * If it is spread across multiple pathnames, like for example - /form/degree-selection, it will return appropriate state name - form.degreeSelection
   * @param {*} pathname
   */
  getState(pathname) {
    let state = (pathname || '').split('/');

    if (state[2]) {
      let tmp = new RegExp('-[a-z]', 'g').exec(state[2]);

      return state[1] + '.' + (tmp ? state[2].replace(tmp[0], tmp[0].slice(1).toUpperCase()) : state[2]);
    }
    return state[1] || 'login';
  }

  setAuthorization() {
    return new Promise((resolve) => {
      // Set token type based on active auth provider
      const activeProviderType = auth.getActiveProviderType();
      if (activeProviderType === 'custom_oauth') {
        this._tokenType = 'CUSTOM_OAUTH';
      } else if (activeProviderType === 'asurite') {
        this._tokenType = 'COGNITO'; // Keep existing AWS token type for ASURITE
      } else {
        this._tokenType = 'NONE';
      }

      this._authorization = auth.getAccessToken();

      resolve(true);
    });
  }

  removeAuth0Token() {
    if (this._$window.localStorage) {
      Object.keys(this._$window.localStorage).forEach((e) => {
        if (e.indexOf('com.auth0.auth') >= 0) delete this._$window.localStorage[e];
      });
    }
  }

  getConfirmationData() {
    return Promise.resolve(this.partnerData.confirmationData);
  }

  setLearnerSub() {
    this.learnerData.isFirstTimeUser = true;
  }

  isCourseElligibleForBenefit(courseStartDate, courseEndDate) {
    if (this.learnerData._eligibilityStartDate && this.learnerData._eligibilityEndDate) {
      return !(
        new Date(this.learnerData._eligibilityStartDate) > new Date(courseEndDate) ||
        new Date(this.learnerData._eligibilityEndDate) < new Date(courseStartDate)
      );
    }
    return false;
  }

  async saveOpenEdxId() {
    try {
      let apiPath = `/api/learner?learnerId=${this.learnerData.learnerId}`,
        method = 'patch';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);

      // set data at salesforce
      let response = await this._doConnect(apiPath, method, {
        openEdxId: this.learnerData.openEdxId,
        journey: JOURNEY_CODES[journey] || 'Update Learners OpenEdxId'
      });

      return response;
    } catch (error) {
      Logger.methods.logError('saveOpenEdx', error);
    }
    return;
  }
  async updateIDVerify(data) {
    try {
      let apiPath = `/api/learner?asuRite=${this.learnerData._asuRite}`,
        method = 'patch';

      const caller = Helper.methods.getCaller();
      const path = (apiPath || '').split('?')[0];
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Update Learners ID Verify Info');

      // set data at salesforce
      let response = await this._doConnect(apiPath, method, data);

      return response;
    } catch (error) {
      Logger.methods.logError('updatedIDVerify', error);
    }
    return;
  }

  async resetCourse(data) {
    try {
      let apiPath = `/api/enrollments/reset-course`,
        method = 'post';

      if (data instanceof Array) {
        for (const item of data) {
          item['isCourseResetRequested'] = item['isCourseResetRequested'] || false;
        }
      }

      const caller = Helper.methods.getCaller();
      const path = apiPath;
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Course Reset');

      let response = await this._doConnect(apiPath, method, data);

      return response;
    } catch (error) {
      Logger.methods.logError('resetCourse', error);
    }
  }

  async patchLearnerContactInfo(data) {
    try {
      let apiPath = `/api/learner?asuRite=${this.learnerData._asuRite}&email=${this.learnerData._emailId}`,
        method = 'patch';

      data.contactId = this.learnerData._contactId;

      const caller = Helper.methods.getCaller();
      const path = apiPath;
      const journey = Helper.methods.getLearnersJourney(caller, path);
      apiPath = Helper.methods.setJourneyInResoursePath(
        apiPath,
        JOURNEY_CODES[journey],
        'Update Learners Contact Info'
      );

      let response = await this._doConnect(apiPath, method, data);
      return response;
    } catch (error) {
      Logger.methods.logError('patchLearnerContactInfo ', error);
    }
    return;
  }

  async requestNameChange(data) {
    try {
      let apiPath = `/api/learner?asuRite=${this.learnerData._asuRite}&email=${this.learnerData._emailId}`,
        method = 'post';

      data.contactId = this.learnerData._contactId;

      const caller = Helper.methods.getCaller();
      const path = apiPath;
      const journey = Helper.methods.getLearnersJourney(caller, path);
      Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Learners Request Name Change');

      let response = await this._doConnect(apiPath, method, data);
      return response;
    } catch (error) {
      Logger.methods.logError('requestNameChange ', error);
    }
  }

  async getEnrollmentById(enrollmentId) {
    if (!enrollmentId) return Promise.resolve({});

    let apiPath = `/api/enrollments?pretty&enrollmentId=${enrollmentId}`,
      method = 'get';
    const caller = Helper.methods.getCaller();
    const path = (apiPath || '').split('?')[0];
    const journey = Helper.methods.getLearnersJourney(caller, path);

    apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'Fetch Enrollment by id');

    let response = await this._doConnect(apiPath, method);
    return response.data;
  }

  async putInvoiceDetails(data) {
    let apiPath = `/api/invoices`,
      method = 'post';
    const caller = Helper.methods.getCaller();
    const path = (apiPath || '').split('?')[0];
    const journey = Helper.methods.getLearnersJourney(caller, path);

    apiPath = Helper.methods.setJourneyInResoursePath(apiPath, JOURNEY_CODES[journey], 'put invoice details');

    let response = await this._doConnect(apiPath, method, data);
    return response;
  }

  async getBlockStatusForASUAdmittedStudent() {
    try {
      // Get learner data from Pinia store
      const { useUserStore } = await import('@/stores/user');
      const userStore = useUserStore();
      const asuriteToLookForBlock = (userStore.apis.learnerData || {})._asuRite || this.learnerData._asuRite;

      const isInvalidAsurite = /^[ 0-9/-]*$/.test(asuriteToLookForBlock);

      if (!asuriteToLookForBlock || isInvalidAsurite) return;

      let apiPath = `/api/get-learner-block-status?asurite=${asuriteToLookForBlock}`;
      let method = 'get';
      const isStudentAllowedResponse = await this._doConnect(apiPath, method);

      return isStudentAllowedResponse.data;
    } catch (error) {
      return '';
    }
  }

  /**
   * Validates a voucher code for a given course enrollment
   * @param {Object} params - Parameters for validating voucher
   * @param {string} params.voucherCode - The voucher code to validate
   * @param {string} params.courseOfferingId - The course offering ID the voucher is being applied to
   * @param {string} params.enrollmentMode - The enrollment mode (credit, verified, honor, audit)
   * @param {string} params.enrollmentId - The enrollment ID if applicable
   * @returns {Promise<Object>} - Object with validation results
   */
  async validateVoucherCode(params) {
    try {
      const apiPath = `/api/enrollments/vouchers/validate`;
      const method = 'post';
      const data = {
        voucherCode: params.voucherCode,
        courseOfferingId: params.courseOfferingId,
        enrollmentMode: params.enrollmentMode,
        enrollmentId: params.enrollmentId,
        learnerId: params.learnerId,
        benefitType: params.benefitType
      };

      const response = await this._doConnect(apiPath, method, data);

      if (!response || !response.data) {
        throw new Error('Invalid voucher');
      }

      const result = response.data.body;

      // First check for fixed discount, then percentage discount
      const fixedDiscount = parseFloat(result.discount) || 0;
      const discountPercentage = parseFloat(result.discountPercentage) || 0;

      return {
        success: true,
        data: {
          valid: fixedDiscount || discountPercentage,
          fixedDiscount: fixedDiscount,
          discountPercentage: discountPercentage,
          message: result.message || 'Voucher code validated successfully'
        }
      };
    } catch (error) {
      Logger.methods.logError('validateVoucherCode', error);
      return {
        success: false,
        data: {
          valid: false,
          message: error.message || 'Failed to validate voucher code'
        }
      };
    }
  }

  /**
   * Sources the Aether/custom-oauth token these bridge calls need — never the
   * ea-app/Cognito token setAuthorization() attaches by default, which Aether rejects.
   * @returns {Promise<string|null>} A usable token, or null if one couldn't be obtained.
   */
  async getAetherBridgeToken() {
    let token = customOAuthService.isAuthenticated() ? customOAuthService.getAccessToken() : null;

    if (!token) {
      try {
        await customOAuthService.silentAuth({ redirectOnUnauthorized: false });
        token = customOAuthService.getAccessToken();
      } catch {
        // Refresh failed — fall through to the ASURITE exchange below.
      }
    }

    if (!token && auth.getActiveProviderType() === 'asurite') {
      try {
        const idToken = auth.getAsuriteIdToken();
        const tokenResponse = await customOAuthService.exchangeAsuriteTokenForTokens(idToken);
        await customOAuthService.localLogin(tokenResponse, null, { emitEvent: false });
        token = customOAuthService.getAccessToken();
      } catch {
        // Exchange failed — caller falls back to skipping the bridge call.
      }
    }

    return token || null;
  }

  /**
   * Validate a promo code through Aether's restriction system
   * @param {Object} params - Validation parameters
   * @param {string} params.promoCode - The promo code to validate
   * @param {string} [params.benefitType] - Learner's benefit type
   * @param {string} [params.purchaseType] - 'enrollment' or 'credit_conversion'
   * @param {string} [params.courseOfferingId] - Course offering UUID
   * @param {string} [params.learnerId] - Aether learner UUID
   * @param {string} [params.term] - Term code (e.g., '2257B')
   * @param {string} [params.partnerName] - Partner name
   * @returns {Promise<Object>} - Validation result with coupon discount details
   */
  async validateAetherPromoCode(params) {
    if (!this.learnerData?._useAetherFlow) {
      return { valid: false, message: 'Stripe payments not enabled' };
    }

    try {
      const aetherToken = await this.getAetherBridgeToken();
      if (!aetherToken) {
        return { valid: false, message: 'Unable to validate promo code right now. Please try again.' };
      }

      const apiPath = '/api/stripe/promo-code/validate';
      const method = 'post';
      const contactId = this.learnerData?._contactId || this.learnerData?.contactId;
      const data = {
        promoCode: params.promoCode,
        ...(contactId && { contactId }),
        context: {
          benefitType: params.benefitType,
          purchaseType: params.purchaseType,
          courseOfferingId: params.courseOfferingId,
          learnerId: params.learnerId,
          term: params.term,
          partnerName: params.partnerName
        }
      };

      // tokentype must match the Aether token above, or ea-app's own auth gate rejects it as an invalid Cognito JWT.
      const response = await this._doConnect(apiPath, method, data, {
        skipAuthRedirect: true,
        headers: { Authorization: `Bearer ${aetherToken}`, tokentype: 'CUSTOM_OAUTH' }
      });

      if (!response || !response.data) {
        throw new Error('Invalid promo code');
      }

      return response.data;
    } catch (error) {
      Logger.methods.logError('validateAetherPromoCode', error);

      // A 401 means the upstream auth check rejected the forwarded token, not that the
      // learner's code is wrong. The raw error here is axios's "Request failed with status
      // code 401", and the caller renders `message` verbatim under the voucher field, so
      // give it something a learner can act on. Deliberately not "Invalid promo code" —
      // the code may well be valid, and we don't know either way.
      if (error.status === 401) {
        return {
          valid: false,
          message: 'Unable to validate promo code right now. Please try again.'
        };
      }

      return {
        valid: false,
        // Prefer the server-provided message over the generic axios one, and surface the
        // Aether error code (STR_4004/STR_4005/...) so the UI can show specific copy.
        // api-hub returns the message under `data.error`; other connector paths use
        // `data.message`, so check both before falling back to the axios message.
        errorCode: error.errorCode || error.response?.data?.errorCode,
        message:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to validate promo code'
      };
    }
  }

  /**
   * Find invisible (auto-apply) promo codes from Aether for a given context
   * @param {Object} params - Context parameters
   * @param {string} [params.benefitType] - Learner's benefit type
   * @param {string} [params.purchaseType] - 'enrollment' or 'credit_conversion'
   * @param {string} [params.courseOfferingId] - Course offering UUID
   * @param {string} [params.learnerId] - Aether learner UUID
   * @param {string} [params.term] - Term code
   * @param {string} [params.partnerName] - Partner name
   * @returns {Promise<Object>} - First valid invisible promo code with discount details, or { valid: false }
   */
  async findInvisiblePromoCodes(params) {
    if (!this.learnerData?._useAetherFlow) {
      return { valid: false };
    }

    try {
      const aetherToken = await this.getAetherBridgeToken();
      if (!aetherToken) {
        return { valid: false };
      }

      const apiPath = '/api/stripe/promo-code/find-invisible';
      const method = 'post';
      const contactId = this.learnerData?._contactId || this.learnerData?.contactId;
      const data = {
        ...(contactId && { contactId }),
        context: {
          benefitType: params.benefitType,
          purchaseType: params.purchaseType,
          courseOfferingId: params.courseOfferingId,
          learnerId: params.learnerId,
          term: params.term,
          partnerName: params.partnerName
        }
      };

      // tokentype must match the Aether token above, or ea-app's own auth gate rejects it as an invalid Cognito JWT.
      const response = await this._doConnect(apiPath, method, data, {
        skipAuthRedirect: true,
        headers: { Authorization: `Bearer ${aetherToken}`, tokentype: 'CUSTOM_OAUTH' }
      });

      if (!response || !response.data) {
        return { valid: false };
      }

      return response.data;
    } catch (error) {
      Logger.methods.logError('findInvisiblePromoCodes', error);
      return { valid: false };
    }
  }

  /**
   * Check if an email exists in the system
   * @param {string} email - The email address to check
   * @returns {Promise<boolean>} - True if email exists, false otherwise
   */
  async checkEmailExists(email, partnerSubdomain) {
    try {
      // Base64 encode the email for privacy
      const encodedEmail = btoa(email);

      let url = this.baseURL() + `/api/email/exists?email=${encodeURIComponent(encodedEmail)}`;
      if (partnerSubdomain) {
        url += `&partnerSubdomain=${encodeURIComponent(partnerSubdomain)}`;
      }

      const response = await this._$http({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data.exists || false;
    } catch (error) {
      Logger.methods.logError('checkEmailExists', error);
      throw error;
    }
  }

  /**
   * Add a course to transcript - POST request
   * @param {Object} params - Parameters for adding to transcript
   * @param {string} params.asurite - ASU Rite ID
   * @param {string} params.enrollmentId - Enrollment ID to add to transcript
   * @returns {Promise<Object>} - Response from the API
   */
  async postAddToTranscript({ asurite, enrollmentId }) {
    try {
      // Ensure environment is set
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      // Set authorization if needed
      await this.setAuthorization();

      const apiPath = `/api/transaction/credit/request/v2`;
      const method = 'post';
      const data = { asurite, enrollmentId };

      const caller = Helper.methods.getCaller();
      const path = apiPath;
      const journey = Helper.methods.getLearnersJourney(caller, path);

      const response = await this._doConnect(apiPath, method, {
        ...data,
        journey: JOURNEY_CODES[journey] || 'Credit Request v2'
      });

      // Handle the new API response format that returns statusCode and body
      if (response.data && response.data.body) {
        // Parse the JSON string in the body field
        const parsedBody = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;

        // Check if the API returned an error status
        if (response.data.statusCode >= 400) {
          throw new Error(parsedBody.message || parsedBody.error || 'Credit request failed');
        }

        return parsedBody;
      }

      // Fallback for direct response format
      return response.data;
    } catch (error) {
      Logger.methods.logError('postAddToTranscript', error);

      // Handle 409 responses with early-retry status
      if (error.status === 409 || error.response?.status === 409) {
        try {
          // Extract JSON payload from error message if present
          // Prioritize response data message over generic axios error message
          const errorMessage = error.response?.data?.message || error.message || '';
          const jsonMatch = errorMessage.match(/Failed with response code:409, ({.+})/);

          if (jsonMatch) {
            try {
              const jsonPayload = JSON.parse(jsonMatch[1]);
              if (jsonPayload.status === 'early-retry') {
                // Return the early-retry status instead of throwing
                return jsonPayload;
              }
            } catch (innerParseError) {
              Logger.methods.logError('Failed to parse extracted JSON from 409 error', innerParseError);
            }
          }
        } catch (parseError) {
          // If parsing fails, continue with normal error throwing
          Logger.methods.logError('Failed to parse 409 error response', parseError);
        }
      }

      throw error;
    }
  }

  /**
   * Get add-to-transcript status - GET request
   * @param {Object} params - Parameters for getting transcript status
   * @param {string} params.asurite - ASU Rite ID
   * @param {string} params.enrollmentId - Enrollment ID to check status for
   * @returns {Promise<Object>} - Response with transcript status
   */
  async getAddToTranscriptStatus({ asurite, enrollmentId }) {
    try {
      // Ensure environment is set
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      // Set authorization if needed
      await this.setAuthorization();

      const apiPath = `/api/transaction/credit/request/v2?asurite=${encodeURIComponent(asurite)}&enrollmentId=${encodeURIComponent(enrollmentId)}`;
      const method = 'get';

      const response = await this._doConnect(apiPath, method);

      // Handle the new API response format that returns statusCode and body
      if (response.data && response.data.body) {
        // Parse the JSON string in the body field
        const parsedBody = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;

        // Check if the API returned an error status
        if (response.data.statusCode >= 400) {
          throw new Error(parsedBody.message || parsedBody.error || 'Credit request failed');
        }

        return parsedBody;
      }

      // Fallback for direct response format
      return response.data;
    } catch (error) {
      Logger.methods.logError('getAddToTranscriptStatus', error);
      throw error;
    }
  }

  /**
   * Check if learner has already used December 2024 campaign discount (ULC-2637)
   * @param {string} learnerId - EA Learner Application Salesforce ID
   * @returns {Promise<Object>} - Response with campaign usage status
   */
  async checkCampaignUsage(learnerId) {
    try {
      // Ensure environment is set
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      // Set authorization if needed
      await this.setAuthorization();

      const apiPath = `/api/enrollments/campaign/usage?learnerId=${encodeURIComponent(learnerId)}`;
      const method = 'get';

      const response = await this._doConnect(apiPath, method);

      // Check if response exists
      if (!response) {
        Logger.methods.logError('checkCampaignUsage', 'No response received from API');
        return {
          campaignActive: false,
          alreadyUsed: false
        };
      }

      // Handle the API response format
      if (response.data && response.data.body) {
        const parsedBody = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;

        if (response.data.statusCode >= 400) {
          throw new Error(parsedBody.message || parsedBody.error || 'Campaign usage check failed');
        }

        return parsedBody;
      }

      // Fallback for direct response format
      if (response.data) {
        return response.data;
      }

      // If no valid data structure, return safe default
      Logger.methods.logError('checkCampaignUsage', 'Unexpected response format', response);
      return {
        campaignActive: false,
        alreadyUsed: false
      };
    } catch (error) {
      Logger.methods.logError('checkCampaignUsage', error);
      // Return safe default instead of throwing
      return {
        campaignActive: false,
        alreadyUsed: false
      };
    }
  }

  /**
   * Get all certificates for a learner
   * @param {Object} params - Query parameters
   * @param {string} params.ea_learner_application - Learner application ID
   * @returns {Promise<Object>} - Response with certificates array
   */
  async getMyCertificates({ ea_learner_application }) {
    try {
      // Ensure environment is set
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      // Set authorization if needed
      await this.setAuthorization();

      const apiPath = `/v2/api/certificates/my-certificates?ea_learner_application=${ea_learner_application}`;
      const method = 'get';

      const response = await this._doConnect(apiPath, method);

      // Handle the API response format
      if (response.data && response.data.body) {
        const parsedBody = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;

        if (response.data.statusCode >= 400) {
          throw new Error(parsedBody.message || parsedBody.error || 'Failed to fetch certificates');
        }

        return parsedBody;
      }

      // Fallback for direct response format
      return response.data;
    } catch (error) {
      Logger.methods.logError('getMyCertificates', error);
      throw error;
    }
  }

  /**
   * Register learner for a certificate program
   * @param {Object} params - Registration parameters
   * @param {string} params.certificate - Certificate URL navigation parameter (e.g., 'aws-cloud-computing')
   * @param {string} params.ea_learner_application - Learner application ID
   * @returns {Promise<Object>} - Registration response with certificate details
   */
  async registerForCertificate({ certificate, ea_learner_application }) {
    try {
      // Ensure environment is set
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      // Set authorization if needed
      await this.setAuthorization();

      const apiPath = `/v2/api/certificates/register`;
      const method = 'post';
      const data = {
        certificate,
        ea_learner_application
      };

      const response = await this._doConnect(apiPath, method, data);

      // Handle the API response format
      if (response.data && response.data.body) {
        const parsedBody = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;

        if (response.data.statusCode >= 400) {
          throw new Error(parsedBody.message || parsedBody.error || 'Certificate registration failed');
        }

        return parsedBody;
      }

      // Fallback for direct response format
      return response.data;
    } catch (error) {
      Logger.methods.logError('registerForCertificate', error);
      throw error;
    }
  }

  /**
   * Unregister learner from a certificate program
   * @param {Object} params - Unregistration parameters
   * @param {number} params.credential_id - Certificate credential ID
   * @param {string} params.ea_learner_application - Learner application ID
   * @returns {Promise<Object>} - Unregistration response
   */
  async unregisterFromCertificate({ credential_id, ea_learner_application }) {
    try {
      // Ensure environment is set
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      // Set authorization if needed
      await this.setAuthorization();

      const apiPath = `/v2/api/certificates/unregister`;
      const method = 'post';
      const data = {
        credential_id,
        ea_learner_application
      };

      const response = await this._doConnect(apiPath, method, data);

      // Handle the API response format
      if (response.data && response.data.body) {
        const parsedBody = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;

        if (response.data.statusCode >= 400) {
          throw new Error(parsedBody.message || parsedBody.error || 'Certificate unregistration failed');
        }

        return parsedBody;
      }

      // Fallback for direct response format
      return response.data;
    } catch (error) {
      Logger.methods.logError('unregisterFromCertificate', error);
      throw error;
    }
  }

  /**
   * Fetch issued POK credentials for a learner from Aether (via ULC API).
   * Returns credentials that can be matched to certificates by pok_template_id.
   * @param {Object} params
   * @param {string} params.ea_learner_application - Learner application ID
   * @returns {Promise<Array>} Array of POK credential objects
   */
  async getPokCredentials({ ea_learner_application }) {
    try {
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      await this.setAuthorization();

      const apiPath = `/v2/api/certificates/pok-credentials?ea_learner_application=${encodeURIComponent(ea_learner_application)}`;
      const response = await this._doConnect(apiPath, 'get');

      if (response.data && response.data.body) {
        const parsedBody = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;

        if (response.data.statusCode >= 400) {
          throw new Error(parsedBody.message || parsedBody.error || 'Failed to fetch POK credentials');
        }

        return parsedBody.credentials || [];
      }

      return response.data?.credentials || [];
    } catch (error) {
      Logger.methods.logError('getPokCredentials', error);
      throw error;
    }
  }

  /**
   * Mark the POK congratulations modal as shown for a credential connection.
   * Persists a DB flag so the modal never fires again across sessions.
   * @param {Object} params
   * @param {number} params.connection_id - credential_learner_connection.id
   * @param {number} params.ea_learner_application - ownership check (IDOR guard)
   */
  async markPokConfettiShown({ connection_id, ea_learner_application }) {
    try {
      if (!this._baseUrl) {
        this.env = window.location.host;
      }

      await this.setAuthorization();

      await this._doConnect('/v2/api/certificates/pok-confetti-shown', 'patch', {
        connection_id,
        ea_learner_application
      });
    } catch (error) {
      // Non-blocking — a failure here must not surface to the user
      Logger.methods.logError('markPokConfettiShown', error);
    }
  }
}

export default new ConnectorService(Axios, window, {}, setInterval);
